import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Users, DollarSign, Calendar, Award, AlertTriangle } from 'lucide-react';

const RHEstadisticas = () => {
  // Datos de ejemplo
  const estadisticas = {
    totalEmpleados: 45,
    empleadosActivos: 42,
    empleadosVacaciones: 3,
    nominaMensual: 1250000,
    antiguedadPromedio: 4.2,
    rotacionAnual: 8.5,
    satisfaccionLaboral: 87
  };

  const departamentos = [
    { nombre: "Operaciones", empleados: 15, presupuesto: 450000 },
    { nombre: "Mantenimiento", empleados: 12, presupuesto: 360000 },
    { nombre: "Logística", empleados: 8, presupuesto: 240000 },
    { nombre: "Transporte", empleados: 6, presupuesto: 180000 },
    { nombre: "Administración", empleados: 4, presupuesto: 120000 }
  ];

  const tendenciaContrataciones = [
    { mes: "Enero", contrataciones: 2, bajas: 1 },
    { mes: "Febrero", contrataciones: 3, bajas: 0 },
    { mes: "Marzo", contrataciones: 1, bajas: 2 },
    { mes: "Abril", contrataciones: 4, bajas: 1 },
    { mes: "Mayo", contrataciones: 2, bajas: 0 },
    { mes: "Junio", contrataciones: 3, bajas: 1 }
  ];

  const indicadoresDesempeno = [
    { indicador: "Asistencia", valor: 95, meta: 90, status: "Excelente" },
    { indicador: "Productividad", valor: 88, meta: 85, status: "Bueno" },
    { indicador: "Capacitación", valor: 72, meta: 80, status: "Requiere atención" },
    { indicador: "Satisfacción", valor: 87, meta: 85, status: "Excelente" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/recursos-humanos">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </motion.button>
          </Link>
          <h1 className="text-4xl font-bold text-gray-800">📊 Estadísticas de RRHH</h1>
        </div>

        {/* KPIs principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white"
          >
            <Users className="w-10 h-10 mb-3 opacity-80" />
            <p className="text-blue-100 text-sm mb-1">Total Empleados</p>
            <p className="text-4xl font-bold">{estadisticas.totalEmpleados}</p>
            <p className="text-blue-100 text-xs mt-2">↑ 12% vs año anterior</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white"
          >
            <DollarSign className="w-10 h-10 mb-3 opacity-80" />
            <p className="text-green-100 text-sm mb-1">Nómina Mensual</p>
            <p className="text-4xl font-bold">${(estadisticas.nominaMensual / 1000).toFixed(0)}K</p>
            <p className="text-green-100 text-xs mt-2">MXN - Enero 2026</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white"
          >
            <Calendar className="w-10 h-10 mb-3 opacity-80" />
            <p className="text-purple-100 text-sm mb-1">Antigüedad Promedio</p>
            <p className="text-4xl font-bold">{estadisticas.antiguedadPromedio}</p>
            <p className="text-purple-100 text-xs mt-2">años</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white"
          >
            <TrendingUp className="w-10 h-10 mb-3 opacity-80" />
            <p className="text-orange-100 text-sm mb-1">Rotación Anual</p>
            <p className="text-4xl font-bold">{estadisticas.rotacionAnual}%</p>
            <p className="text-orange-100 text-xs mt-2">↓ 3% vs año anterior</p>
          </motion.div>
        </div>

        {/* Distribución por departamento */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Users className="w-6 h-6 text-green-500" />
            Distribución por Departamento
          </h2>
          <div className="space-y-4">
            {departamentos.map((dept, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-40 text-sm font-semibold text-gray-700">{dept.nombre}</div>
                <div className="flex-1">
                  <div className="h-8 bg-gray-200 rounded-lg overflow-hidden relative">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(dept.empleados / estadisticas.totalEmpleados) * 100}%` }}
                      transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                      className="h-full bg-gradient-to-r from-green-400 to-green-600 flex items-center justify-end pr-3"
                    >
                      <span className="text-white text-xs font-bold">{dept.empleados} empleados</span>
                    </motion.div>
                  </div>
                </div>
                <div className="w-32 text-right text-sm text-gray-600">
                  ${(dept.presupuesto / 1000).toFixed(0)}K/mes
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Tendencia de contrataciones */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-blue-500" />
              Tendencia 2026
            </h2>
            <div className="space-y-3">
              {tendenciaContrataciones.map((dato, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-semibold text-gray-700">{dato.mes}</span>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Ingresos:</span>
                      <span className="text-green-600 font-bold">+{dato.contrataciones}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Bajas:</span>
                      <span className="text-red-600 font-bold">-{dato.bajas}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Indicadores de desempeño */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Award className="w-6 h-6 text-purple-500" />
              Indicadores de Desempeño
            </h2>
            <div className="space-y-4">
              {indicadoresDesempeno.map((ind, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">{ind.indicador}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-gray-800">{ind.valor}%</span>
                      {ind.valor >= ind.meta ? (
                        <Award className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-orange-500" />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          ind.valor >= ind.meta ? 'bg-green-500' : 'bg-orange-500'
                        }`}
                        style={{ width: `${ind.valor}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500">Meta: {ind.meta}%</span>
                  </div>
                  <p className={`text-xs font-semibold ${
                    ind.valor >= ind.meta ? 'text-green-600' : 'text-orange-600'
                  }`}>
                    {ind.status}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RHEstadisticas;
